import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Validators, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { map } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { Field } from '../models/field.model';

@Component({
	selector: 'field-generator',
	templateUrl: './field-generator.component.html',
	styleUrls: ['./field-generator.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldGeneratorComponent implements OnInit, OnDestroy {
	@Input() field: Field;
	@Input() form: UntypedFormGroup;
	errorMessages = {
		required: '',
		minLength: '',
		maxLength: ''
	};

	get isValid() {
		const ctrl = this.form.controls[this.field.name];
		if (ctrl) {
			return ctrl.valid;
		} else {
			return true;
		}
	}
	get isDirty() {
		const ctrl = this.form.controls[this.field.name];
		if (ctrl) {
			return ctrl.dirty;
		} else {
			return true;
		}
	}
	get fieldErrors() {
		const ctrl = this.form.controls[this.field.name];
		if (ctrl) {
			return ctrl.errors;
		} else {
			return true;
		}
	}
	get showRequiredSign() {
		return this.field.validators.map(val => val.type).some(type => type === 'required');
	}
	isHidden$: Observable<boolean>;
	isDisabled$: Observable<boolean>;
	removeCtrlSub: Subscription;
	removeCtrl$;
	ctrlValue;

	constructor() {}

	ngOnInit() {
		const validators = this.field.validators.map(val => this.handleValidator(val));
		this.form.controls[this.field.name]?.setValidators(validators);
		this.isHidden$ = this.form?.valueChanges?.pipe(map(val => this.field.hidden || this.isHiddenWhen()));
		this.isDisabled$ = this.form?.valueChanges?.pipe(map(val => this.field.disabled || this.isDisabledWhen()));
		this.removeCtrl$ = this.form?.valueChanges?.pipe(map(() => this.removeControlWhen()));
		// this.form.controls[this.field.name].updateValueAndValidity();
	}

	isHiddenWhen() {
		if (!!this.field.hiddenWhen) {
			return this.field.hiddenWhen
				.map(cond => {
					const fieldControl = this.form.controls[cond.field];
					switch (cond.operator) {
						case 'is': {
							if (cond.value === 'valid') {
								return fieldControl.valid ? true : false;
							} else if (cond.value === 'invalid') {
								return fieldControl.invalid ? true : false;
							}
							return false;
						}
						case 'equals': {
							return fieldControl.value === cond.value ? true : false;
						}
						case 'contains': {
							return fieldControl?.value?.includes(cond.value) ? true : false;
						}
					}
				})
				.includes(true);
		}
	}

	isDisabledWhen() {
		if (!!this.field.disableWhen) {
			return this.field.disableWhen
				.map(cond => {
					const fieldControl = this.form.controls[cond.field];
					switch (cond.operator) {
						case 'is': {
							if (cond.value === 'valid') {
								return fieldControl.valid ? true : false;
							} else if (cond.value === 'invalid') {
								return fieldControl.invalid ? true : false;
							}
							return false;
						}
						case 'equals': {
							return fieldControl.value === cond.value ? true : false;
						}
						case 'contains': {
							return fieldControl?.value?.includes(cond.value) ? true : false;
						}
					}
				})
				.includes(true);
		}
	}

	removeControlWhen() {
		if (!!this.field.removeControlWhen) {
			return this.field.removeControlWhen
				.map(cond => {
					const fieldControl = this.form.controls[cond.field];
					const ctrl = this.form.controls[this.field.name];
					// this.ctrlValue = ctrl.value;
					switch (cond.operator) {
						case 'is': {
							if (cond.value === 'valid') {
								if (fieldControl.valid) {
									if (ctrl) {
										this.ctrlValue = ctrl.value;
										this.form.removeControl(this.field.name);
									}
									return true;
								} else {
									if (!ctrl) {
										// tslint:disable-next-line: max-line-length
										this.form.addControl(
											this.field.name,
											new UntypedFormControl(
												this.ctrlValue,
												this.field.validators.map(val => this.handleValidator(val))
											)
										);
										this.form.controls[this.field.name]?.markAsDirty();
										this.form?.updateValueAndValidity();
									}
									return false;
								}
							} else if (cond.value === 'invalid') {
								if (fieldControl.invalid) {
									if (ctrl) {
										this.ctrlValue = ctrl.value;
										this.form.removeControl(this.field.name);
									}
									return true;
								} else {
									if (!ctrl) {
										// tslint:disable-next-line: max-line-length
										this.form.addControl(
											this.field.name,
											new UntypedFormControl(
												this.ctrlValue,
												this.field.validators.map(val => this.handleValidator(val))
											)
										);
										this.form.controls[this.field.name]?.markAsDirty();
										this.form?.updateValueAndValidity();
									}
									return false;
								}
							}
							break;
						}
						case 'equals': {
							if (fieldControl.value === cond.value) {
								if (ctrl) {
									this.ctrlValue = ctrl.value;
									this.form.removeControl(this.field.name);
								}
								return true;
							} else {
								if (!ctrl) {
									// tslint:disable-next-line: max-line-length
									this.form.addControl(
										this.field.name,
										new UntypedFormControl(
											this.ctrlValue,
											this.field.validators.map(val => this.handleValidator(val))
										)
									);
									this.form.controls[this.field.name]?.markAsDirty();
									this.form?.updateValueAndValidity();
								}
								return false;
							}
							break;
						}
						case 'contains': {
							if (fieldControl?.value?.includes(cond.value)) {
								if (ctrl) {
									this.ctrlValue = ctrl.value;
									this.form.removeControl(this.field.name);
								}
								return true;
							} else {
								if (!ctrl) {
									// tslint:disable-next-line: max-line-length
									this.form.addControl(
										this.field.name,
										new UntypedFormControl(
											this.ctrlValue,
											this.field.validators.map(val => this.handleValidator(val))
										)
									);
									this.form.controls[this.field.name]?.markAsDirty();
									this.form?.updateValueAndValidity();
								}
								return false;
							}
							break;
						}
					}
				})
				.includes(true);
		}
	}

	handleValidator(validator) {
		switch (validator.type) {
			case 'required': {
				this.errorMessages.required = validator.errorMessage;
				return Validators.required;
			}
			case 'minLength': {
				this.errorMessages.minLength = validator.errorMessage;
				return Validators.minLength(validator.value);
			}
			case 'maxLength': {
				this.errorMessages.maxLength = validator.errorMessage;
				return Validators.maxLength(validator.value);
			}
		}
	}

	ngOnDestroy() {
		if (this.removeCtrlSub) {
			this.removeCtrlSub.unsubscribe();
		}
	}
}
